import Flex from 'UIKit/Box/Flex'

import AlchemyModal, { ModalBody, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import Text from 'UIKit/Text'
import { BIG_ZERO } from 'config/constants/number'
import { TokenAmount } from 'config/types'
import useModal from 'hooks/useModal'
import React, { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { ModalColorTheme } from 'theme/modal/colors'
import colors from 'theme/ui/colors'
import { getFullDisplayFixedDisplayBalance } from 'utils/formatBalance'
import { generateMineGameMultiplier } from '../../config/utils'

interface MineEstimateProfitModalDetailsProps {
  mines: number
  betAmount: TokenAmount
  current: number
}

const MineEstimateProfitModalDetails: React.FC<ModalProps<MineEstimateProfitModalDetailsProps>> = ({
  data: { betAmount, mines, current },
  onDismiss,
}) => {
  const profits = useMemo(() => {
    return generateMineGameMultiplier(mines)
  }, [mines])

  const amount = useMemo(() => {
    return betAmount ? (betAmount.amount.gt(0) ? betAmount.amount : BIG_ZERO) : BIG_ZERO
  }, [betAmount])

  return (
    <AlchemyModal minWidth={['', '', '380px !important']} onDismiss={onDismiss} colorTheme={ModalColorTheme.Darkblue}>
      <ModalHeader colorTheme={ModalColorTheme.Darkblue} />
      <ModalBody>
        <Flex pb="12px">
          <Flex width="60px">
            <Text
              letterSpacing="-0.28px"
              textAlign="left"
              fontSize="14px"
              lineHeight="20px"
              color="textAlt1"
              fontWeight={700}
            >
              <Trans>Diamonds</Trans>
            </Text>
          </Flex>

          <Flex flex="1 1">
            <Text
              width="100%"
              letterSpacing="-0.28px"
              textAlign="right"
              fontSize="14px"
              lineHeight="20px"
              color="textAlt1"
              fontWeight={700}
            >
              <Trans>Multiplier</Trans>
            </Text>
          </Flex>

          <Flex flex="1 1">
            <Text
              width="100%"
              letterSpacing="-0.28px"
              textAlign="right"
              fontSize="14px"
              lineHeight="20px"
              color="textAlt1"
              fontWeight={700}
            >
              <Trans>Payout</Trans>
            </Text>
          </Flex>
        </Flex>
        <Box maxHeight="600px" overflowY="auto">
          {profits.map((item, index) => (
            <Flex key={index} background={index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.1)'} borderRadius="5px">
              <Flex width="60px">
                <Text
                  display="inline-block"
                  small
                  bold
                  padding={['8px', '8px', '12px']}
                  textAlign="center"
                  color={index === current ? `${colors.primary}` : colors.text}
                >
                  {index + 1}
                </Text>
              </Flex>

              <Flex flex="1 1">
                <Text
                  display="inline-block"
                  fontSize="12px"
                  bold
                  padding={['8px', '8px', '12px']}
                  width="100%"
                  textAlign="right"
                  color={index === current ? `${colors.primary}` : colors.text}
                >
                  {getFullDisplayFixedDisplayBalance(item, 0, {
                    max: 3,
                    min: 3,
                    padZero: true,
                  })}
                  x
                </Text>
              </Flex>

              <Flex flex="1 1">
                <Text
                  display="inline-block"
                  padding={['8px', '8px', '12px']}
                  fontSize="12px"
                  bold
                  small
                  width="100%"
                  textAlign="right"
                  color={
                    index === current
                      ? `${colors.primary}`
                      : profits.length - 1 === index && amount.gt(0)
                      ? colors.success
                      : colors.text
                  }
                >
                  {getFullDisplayFixedDisplayBalance(amount.multipliedBy(item).toString(10), 0, {
                    max: 2,
                    min: 2,
                    padZero: true,
                  })}
                </Text>
              </Flex>
            </Flex>
          ))}
        </Box>
      </ModalBody>
    </AlchemyModal>
  )
}

export const MINE_ESTIMATE_PROFIT_MODAL_KEY = 'mine-est-profit'

const useMineEstProfitModal = () => {
  const modal = useModal(MineEstimateProfitModalDetails, MINE_ESTIMATE_PROFIT_MODAL_KEY)

  return modal
}

export default useMineEstProfitModal
